import React, {useState, useEffect} from 'react';

import AdminMenu from '../../../../components/Admin/MenuDetalhes/AdminMenu';
import { IoMdCheckmark, IoMdClipboard, IoMdCloseCircleOutline, IoMdOpen } from 'react-icons/io';

import {
  Container,
  Title,
  ContentContainer,
  VerticalSeparator,
  FormSelectorContainer,
  FormSelectorFormsContainer,
  FormSelectorItemButton,
  FormSelectorItemsContainer,
  FormActionsContainer,
  HorizontaSeparator
} from './styles';
import {SearchInput, Checkbox, DateInput, RenderIf, Button, TextEditor} from '../../../../components';
import { useDebounce } from '../../../../hooks/useDebounce';
import { promiseHandler } from '../../../../utils/promises';
import { producaov2 } from '../../../../services/apis';

import {
  CourseEventForms,
  CourseForms,
  EmentaForms,
  FormationForms,
  InCompanyForms,
  SpecialistLeanSixSigmaForms,
  CustomCertificateForms
} from './forms';
import { CertificateProvider, useCertificate } from './providers';
import { useLocation, useParams } from 'react-router-dom';

const useSearchResults = ({request = () => {}, resultExtractor = () => {}}) => {
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState([]);

  const debouncedInput = useDebounce(input);

  useEffect(() => {
    const getResults = async () => {
      if(input?.length === 0) {
        return;
      }

      const {data: response, error} = await promiseHandler(request(debouncedInput));
      
      const normalizedResult = response?.data?.map(resultExtractor);

      setResults(normalizedResult);
      setIsLoading(false);
    }

    getResults();
  }, [debouncedInput]);

  useEffect(() => {
    if(!isLoading && input?.length > 0) {
      setIsLoading(true);
      setResults([]);
    }
  }, [input]);

  return {
    value: input,
    onChangeValue: setInput,
    isLoading,
    results
  }
}

const defaultCertificateOptions = {
  userId: null, 
  courseId: null, 
  needRegenerate: true, 
  manualConclusionDate: '', 
  hideConclusionDate: false,
  isEnglish: false,
  internalProjectIds: [],
  externalProjectsName: [],
  includeEmenta: true,
  onlyEmenta: false,
  manualSignaturesId: [],
  eventCourseDays: ''
}

const useCertificateForm = () => {
  const [options, setOptions] = useState(defaultCertificateOptions);

  const onUpdateOption = (optionName = '', value) => {
    setOptions(prev => ({...prev, [optionName]: value}));
  }

  const submit = async () => {
    return await promiseHandler(producaov2.post('admin/certificate', options));
  }

  return {options, onUpdateOption, submit};

}

const UserInput = ({
  onChangeUserSelected = () => {}
}) => {
  const {value, onChangeValue, isLoading, results} = useSearchResults({
    request: (search) => producaov2.get('alunos', {
      params: {
        search,
        invertOrdering: true
      }
    }),
    resultExtractor: user => ({
      id: user?.id,
      text: `${user?.id} - ${user?.nome}`
    })
  });

  return (
    <SearchInput
      label="Aluno"
      placeholder="Digite o id, email ou nome do aluno"
      value={value}
      onChangeText={onChangeValue}
      isLoading={isLoading}
      results={results}
      onResultPress={onChangeUserSelected}
      onClearSelectedResultPress={() => onChangeUserSelected(null)}
    />
  )
}

const CourseInput = ({
  onChangeCourseSelected = () => {}
}) => {

  const {value, onChangeValue, isLoading, results} = useSearchResults({
    request: (search) => producaov2.get('cursos-simplelist-new', {
      params: {
        search,
        limit: 15,
      }
    }),
    resultExtractor:course => ({
      id: course?.id,
      text: `${course?.id} - ${course?.curso}`
    })
  });

  return (
    <SearchInput
      label="Curso"
      placeholder="Digite o id ou nome do curso"
      value={value}
      onChangeText={onChangeValue}
      isLoading={isLoading}
      results={results}
      onResultPress={onChangeCourseSelected}
      onClearSelectedResultPress={() => onChangeCourseSelected(null)}
    />
  )
}

function IsEnglishCheckbox({
  isSelected = false,
  onSelectionPress = () => {}
}) {
  return (
    <Checkbox
      text="Gerar em inglês?"
      isSelected={isSelected}
      onSelectionPress={onSelectionPress}
      helpText="Opção para gerar o certificado em inglês"  
    />
  );
}

function RegenerateCheckbox({
  isSelected = false,
  onSelectionPress = () => {}
}) {
  return (
    <Checkbox
      text="Gerar novamente?"
      isSelected={isSelected}
      onSelectionPress={onSelectionPress}
      helpText="Opção para gerar um novo certificado e sobrescrever caso já tenha um gerado"  
    />
  );
}

function HideConclusionDateCheckbox({
  isSelected = false,
  onSelectionPress = () => {}
}) {
  return (
    <Checkbox
      text="Ocultar data de conclusão"
      isSelected={isSelected}
      onSelectionPress={onSelectionPress}
      helpText="Opção para gerar o certificado sem a data de conclusão"  
    />
  );
}

function OnlyEmentaCheckbox({
  isSelected = false,
  onSelectionPress = () => {}
}) {
  return (
    <Checkbox
      text="Somente ementa?"
      isSelected={isSelected}
      onSelectionPress={onSelectionPress}
      helpText="Opção para gerar somente a ementa do certificado"  
    />
  );
}

function IncludeEmentaCheckbox({
  isSelected = false,
  onSelectionPress = () => {}
}) {
  return (
    <Checkbox
      text="Incluir ementa?"
      isSelected={isSelected}
      onSelectionPress={onSelectionPress}
      helpText="Opção para gerar o certificado com a ementa"  
    />
  );
}

function FormActions({
  certificateUrl = '',
  isLoading = false,
  onSubmit = () => {},
  onClearPress = () => {}
}) {
  const [isLinkCopied, setIsLinkCopied] = useState(false);

  const onCopyLinkPress = () => {
    navigator.clipboard.writeText(certificateUrl);
    setIsLinkCopied(true);
    setTimeout(() => {
      setIsLinkCopied(false); 
    }, 1000);
  }

  return (
    <FormActionsContainer>
      <RenderIf isTrue={!certificateUrl}>
        <Button loading={isLoading} loadingPlaceholder="Gerando ..." text={"Gerar"} onPress={onSubmit} />
      </RenderIf>
      <RenderIf isTrue={!!certificateUrl}>
        <Button
          LeadingIconElement={isLinkCopied ? <IoMdCheckmark /> : <IoMdClipboard />}
          text={isLinkCopied ? "Copiado" : "Copiar Link"}
          type="primary"
          mainColor={isLinkCopied ? '#32CD32' : '#F47C34'}
          minorColor="#FFF"
          onPress={onCopyLinkPress}
        />
        <HorizontaSeparator />
        <Button type="secondary" text="Abrir Link" onPress={() => window.open(certificateUrl)} TrailingIconElement={<IoMdOpen/>}/>
        <HorizontaSeparator />
        <Button type="tertiary" text="Limpar" onPress={onClearPress} TrailingIconElement={<IoMdCloseCircleOutline/>}/>
      </RenderIf>
    </FormActionsContainer>
  )
}

function CourseCertificateForms({
  onClearCertificatePress = () => {}
}) {
  const certificate = useCertificateForm();

  const [certificateUrl, setCertificateUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const onSubmit = async () => {
    setIsLoading(true);

    const {data: response, error} = await certificate.submit();

    setIsLoading(false);

    if(response?.data?.url) {
      setCertificateUrl(response?.data?.url);
    } 
  }

  return (
    <>
      <UserInput
        onChangeUserSelected={(user) => certificate.onUpdateOption('userId', user?.id)}
      />
      <VerticalSeparator />
      <CourseInput
        onChangeCourseSelected={(course) => certificate.onUpdateOption('courseId', course?.id)}
      />
      <VerticalSeparator />
      <DateInput
        label="Data de emissão (Opcional)"
        date={certificate?.options?.manualConclusionDate}
        onChangeDate={(date) => certificate.onUpdateOption('manualConclusionDate', date)}
      />
      <VerticalSeparator />
      <IncludeEmentaCheckbox
        isSelected={certificate.options?.includeEmenta}
        onSelectionPress={() => certificate.onUpdateOption('includeEmenta', !certificate.options?.includeEmenta)}
      />
      <RegenerateCheckbox
        isSelected={certificate.options?.needRegenerate}
        onSelectionPress={() => certificate.onUpdateOption('needRegenerate', !certificate.options?.needRegenerate)}
      />
      <IsEnglishCheckbox
        isSelected={certificate.options?.isEnglish}
        onSelectionPress={() => certificate.onUpdateOption('isEnglish', !certificate.options?.isEnglish)}
      />
      <HideConclusionDateCheckbox
        isSelected={certificate.options?.hideConclusionDate}
        onSelectionPress={() => certificate.onUpdateOption('hideConclusionDate', !certificate.options?.hideConclusionDate)}
      />
      <OnlyEmentaCheckbox
        isSelected={certificate.options?.onlyEmenta}
        onSelectionPress={() => certificate.onUpdateOption('onlyEmenta', !certificate.options?.onlyEmenta)}
      />
      <VerticalSeparator />
      <FormActions
        certificateUrl={certificateUrl}
        onSubmit={onSubmit}
        onClearPress={onClearCertificatePress}
        isLoading={isLoading}
      />
    </>
  );
}

function FormationCertificateForms({
  onClearCertificatePress = () => {}
}) {
  const certificate = useCertificateForm();

  const [certificateUrl, setCertificateUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const onSubmit = async () => {
    setIsLoading(true);

    const {data: response, error} = await certificate.submit();

    setIsLoading(false);

    if(response?.data?.url) {
      setCertificateUrl(response?.data?.url);
    } 
  }

  return (
    <>
      <UserInput
        onChangeUserSelected={(user) => certificate.onUpdateOption('userId', user?.id)}
      />
      <VerticalSeparator />
      <CourseInput
        onChangeCourseSelected={(course) => certificate.onUpdateOption('courseId', course?.id)}
      />
      <VerticalSeparator />
      <DateInput
        label="Data de emissão (Opcional)"
        date={certificate?.options?.manualConclusionDate}
        onChangeDate={(date) => certificate.onUpdateOption('manualConclusionDate', date)}
      />
      <VerticalSeparator />
      <TextEditor />
      <VerticalSeparator />
      <IncludeEmentaCheckbox
        isSelected={certificate.options?.includeEmenta}
        onSelectionPress={() => certificate.onUpdateOption('includeEmenta', !certificate.options?.includeEmenta)}
      />
      <RegenerateCheckbox
        isSelected={certificate.options?.needRegenerate}
        onSelectionPress={() => certificate.onUpdateOption('needRegenerate', !certificate.options?.needRegenerate)}
      />
      <HideConclusionDateCheckbox
        isSelected={certificate.options?.hideConclusionDate}
        onSelectionPress={() => certificate.onUpdateOption('hideConclusionDate', !certificate.options?.hideConclusionDate)}
      />
      <OnlyEmentaCheckbox
        isSelected={certificate.options?.onlyEmenta}
        onSelectionPress={() => certificate.onUpdateOption('onlyEmenta', !certificate.options?.onlyEmenta)}
      />
      <VerticalSeparator />
      <FormActions
        certificateUrl={certificateUrl}
        onSubmit={onSubmit}
        onClearPress={onClearCertificatePress}
        isLoading={isLoading}
      />
    </>
  );
}

function FormSelector({
  children,
  types = [],
  onChangeType,
  selectedType
}) {

  return (
    <FormSelectorContainer>
      <FormSelectorItemsContainer>
        {types?.map(type => {
          if(!type?.FormElement) {
            return null;
          }

          return (
            <FormSelectorItemButton
              onClick={() => onChangeType(type)}
              isSelected={selectedType?.id === type?.id}
            >
              {type?.title}
            </FormSelectorItemButton>
          );
        })}
      </FormSelectorItemsContainer>
      <FormSelectorFormsContainer>
        {children}
      </FormSelectorFormsContainer>
    </FormSelectorContainer>
  );
}

function FormSwitcher({
  selectedType,
  manualUserId,
  manualCourseId
}) {
  
  const {certificatesCount, reset, updateOptions} = useCertificate();

  const Element = selectedType?.FormElement;

  useEffect(() => {
    reset();

    updateOptions({optionName: 'isInCompany', optionValue: (selectedType?.id === 'in-company')});

  }, [selectedType]);

  return (
    <Element
      key={`${selectedType?.id}-${certificatesCount}`}
      manualUserId={manualUserId}
      manualCourseId={manualCourseId}
    />
  )
}

const certificateTypes = [
  {
    id: 'course',
    title: 'Curso',
    FormElement: CourseForms
  },
  {
    id: 'formation',
    title: 'Formação',
    FormElement: FormationForms
  },
  {
    id: 'event',
    title: 'Evento',
    FormElement: CourseEventForms
  },
  {
    id: 'specialist-lean-six-sigma',
    title: 'Especialista Lean Seis Sigma',
    FormElement: SpecialistLeanSixSigmaForms
  },
  {
    id: 'in-company',
    title: 'In Company',
    FormElement: InCompanyForms
  },
  {
    id: 'custom',
    title: 'Customizado',
    FormElement: CustomCertificateForms
  },
  {
    id: 'ementa',
    title: 'Ementa',
    FormElement: EmentaForms
  },
];

export function CreateCertificate() {
  const [type, setType] = useState(certificateTypes[0]);
  const location = useLocation();

  const query = React.useMemo(() => new URLSearchParams(location.search), [location.search]);

  const userId = query.get('userId');
  const courseId = query.get('courseId');

  useEffect(() => {
    const typeId = query.get('type');
    if(typeId) {
      const knownType = certificateTypes.find(type => type.id === typeId);

      if(knownType) {
        setType(knownType);
      }
    }
  }, [query])

  return (
    <Container>
      <AdminMenu />
      <ContentContainer>
        <Title>Gerar Certificado</Title>
        <CertificateProvider>
          <FormSelector
            types={certificateTypes}
            selectedType={type}
            onChangeType={setType}
          >
            <FormSwitcher
              selectedType={type}
              manualUserId={userId}
              manualCourseId={courseId}
            />
          </FormSelector>
        </CertificateProvider>
      </ContentContainer>
    </Container>
  );
}